import { useGetUsersByOrg } from "@hooks";
import { useEffect, useState } from "react";
import { UserState } from "../../utils";

import { BaseUser } from "@models";
import { FormData } from "@types";
import { DocumentVersionApprover } from "src/stores/models/document";
import { AssigneesDisplay } from "./AssigneesDisplay";
import { InviteUserModal } from "./InviteUserModal";
import { includeAuthorInApprovers } from "./helpers";

export type CommonDisplayProps = {
  isEditMode: boolean;
  formData: FormData;
  saving: boolean;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  savedApprovers: DocumentVersionApprover[];
  currentUser: BaseUser | null;
};

export const AssignApprovers = ({
  savedApprovers,
  isEditMode,
  organizationId,
  saving,
  formData,
  setFormData,
  currentUser,
}: CommonDisplayProps & {
  organizationId: string;
}) => {
  const [users, setUsers] = useState<UserState[]>([]);
  const [authorSelectUsers, setAuthorSelectUsers] = useState<UserState[]>([]);
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);
  const [inviteAs, setInviteAs] = useState<"author" | "approver" | null>(null);

  const { data: organizationUsers } = useGetUsersByOrg({
    orgId: organizationId,
  });

  useEffect(() => {
    if (organizationUsers) {
      // Filter out users who are already selected as approvers
      const approverIds = formData.selectedUsers.map(
        (approver) => approver?.id
      );
      const filteredOutApprovers = organizationUsers.filter(
        (user): user is BaseUser =>
          user !== null && !approverIds.includes(user.id)
      );

      setUsers(filteredOutApprovers);

      const filterAuthor = organizationUsers.filter(
        (user): user is BaseUser =>
          user !== null && user.id !== formData.selectedAuthor?.id
      );

      setAuthorSelectUsers(filterAuthor);
    }
  }, [organizationUsers, savedApprovers, formData.selectedUsers]);

  const handleUserInvited = (newUser: BaseUser) => {
    const source = inviteAs;
    const newUsers = [
      ...(formData.selectedUsers || []).filter(
        (u): u is BaseUser => u !== null
      ),
      newUser,
    ];

    // Auto-select the new user based on which select opened the modal
    if (source === "author") {
      handleAuthorChange(newUser);
    } else if (source === "approver") {
      handleApproversChange(newUsers);
    }
  };

  const handleOpenInviteModal = (source: "author" | "approver") => {
    setInviteAs(source);
    setInviteUserModalOpen(true);
  };

  const handleAuthorChange = (newValue: BaseUser | null) => {
    const selectedAuthor = newValue;
    setFormData((prev) => {
      // Remove old author from selectedUsers
      const usersWithoutOldAuthor = prev.selectedUsers.filter(
        (u) => u?.id !== prev.selectedAuthor?.id
      );
      const updatedUsers = includeAuthorInApprovers(
        usersWithoutOldAuthor,
        selectedAuthor
      );

      return {
        ...prev,
        selectedAuthor,
        selectedUsers: updatedUsers,
      };
    });
  };

  const handleApproversChange = (newValue: (BaseUser | null)[] | null) => {
    const filteredValue = (newValue || []).filter(
      (u): u is BaseUser => u !== null
    );
    const finalValue = includeAuthorInApprovers(
      filteredValue,
      formData.selectedAuthor
    );

    setFormData((prev) => ({ ...prev, selectedUsers: finalValue }));
  };

  const commonDisplayProps = {
    isEditMode,
    formData,
    saving,
    setFormData,
    currentUser,
    users,
    savedApprovers,
  };

  return (
    <div>
      <InviteUserModal
        open={inviteUserModalOpen}
        onClose={() => {
          setInviteUserModalOpen(false);
          setInviteAs(null);
        }}
        orgId={organizationId}
        onUserInvited={handleUserInvited}
      />
      <AssigneesDisplay
        {...commonDisplayProps}
        onOpenInviteModal={() => handleOpenInviteModal("author")}
        authorSelectUsers={authorSelectUsers}
        setAuthorSelectUsers={setAuthorSelectUsers}
        handleAuthorChange={handleAuthorChange}
        title={"Author"}
        isAuthor={true}
      />
      <AssigneesDisplay
        {...commonDisplayProps}
        onOpenInviteModal={() => handleOpenInviteModal("approver")}
        handleApproversChange={handleApproversChange}
        title={"Approvers"}
        isAuthor={false}
      />
    </div>
  );
};

export default AssignApprovers;
